import React, { useState, useEffect, useRef } from 'react';
import {
	Button,
	Col,
	Dropdown,
	Form,
	Input,
	Modal,
	Radio,
	Row,
	Table,
	Tag,
	message,
} from 'antd';
import { debounce } from 'lodash';
import { IoSearchOutline } from 'react-icons/io5';

import moment from 'moment';

import { api } from '../../../api/api';
import SuccessIcon from '../../../assets/successicon.svg';

import styles from './creationStatus.module.scss';
import { BsThreeDotsVertical } from 'react-icons/bs';

const CreationStatus = () => {
	const userDetails = JSON.parse(localStorage.getItem('user-onboard-data'));

	const [form] = Form.useForm();

	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [searchText, setSearchText] = useState('');
	const [updateModal, setUpdateModal] = useState(null);

	const [update, setUpdate] = useState({
		loading: false,
		success: null,
		error: null,
	});

	const [tableParams, setTableParams] = useState({
		pagination: {
			current: 1,
			pageSize: 10,
			showSizeChanger: true,
			showQuickJumper: true,
			total: 0,
			// showTotal: (total) => `Total ${total} items`,
		},
	});

	// Fetch the data from API
	const fetchCreationStatus = async () => {
		setLoading(true);
		try {
			const { current, pageSize } = tableParams.pagination;
			const response = await api.get({
				path: '/getcreationbatchstatus',
				params: { page: current, size: pageSize, searchText },
				service: 'useronboarding',
			});

			if (response.status === 200) {
				const pendinglist = response.data?.data;
				const totaldata = response.data?.pagination?.totalData;

				setData(pendinglist);

				setTableParams((prev) => ({
					...prev,
					pagination: {
						...prev.pagination,
						total: totaldata || response.data?.data?.length,
					},
				}));
			}
		} catch (error) {
			message.error('Something went wrong!');
		} finally {
			setLoading(false);
		}
	};

	const handleTableChange = (pagination) => {
		setTableParams((prev) => ({
			...prev,
			pagination,
		}));
	};

	const handleSearch = (e) => {
		if (e.target.value.length >= 3) {
			setSearchText(e.target.value);
			setTableParams((prev) => ({
				...prev,
				pagination: {
					...prev.pagination,
					current: 1,
				},
			}));
		}
	};

	useEffect(() => {
		fetchCreationStatus(); //eslint-disable-next-line
	}, [
		tableParams.pagination.current,
		tableParams.pagination.pageSize,
		searchText,
	]);

	const debouncedSearch = useRef(debounce(handleSearch, 300)).current;

	useEffect(() => {
		return () => {
			debouncedSearch.cancel();
		}; //eslint-disable-next-line
	}, []);

	const renderStatus = (value) => {
		switch (value) {
			case true:
				return <Tag color="green">Yes</Tag>;
			case false:
				return <Tag color="red">No</Tag>;
			default:
				return null;
		}
	};

	const renderRowActions = (row) => {
		const isAdmin = userDetails?.isAdmin === 'Y';
		const {
			isAdcreationSuccess,
			isClinicalcreationSuccess,
			isSsocreationSuccess,
			//isSspcreationSuccess,
		} = row;

		// Only show dropdown if the user is an admin and the status is 'P' or 'R'
		if (!isAdmin) return null;

		const rowActionsMenuItems = [];

		// Show Approve & Reject options only for admins when status is Pending
		if (
			isAdmin &&
			(!isAdcreationSuccess ||
				!isClinicalcreationSuccess ||
				!isSsocreationSuccess )
				//!isSspcreationSuccess)
		) {
			rowActionsMenuItems.push({
				key: '1',
				label: (
					<span
						className={styles['dropdown-options']}
						onClick={() => {
							setUpdateModal(row);
							form.setFieldsValue({
								errorMessage: row?.errorMessage,
								isAdcreationSuccess: row?.isAdcreationSuccess,
								isClinicalcreationSuccess: row?.isClinicalcreationSuccess,
								isSsocreationSuccess: row?.isSsocreationSuccess,
								//isSspcreationSuccess: row?.isSspcreationSuccess,
							});
						}}
					>
						Update
					</span>
				),
			});
		}

		return (
			<Dropdown menu={{ items: rowActionsMenuItems }} placement="bottomRight">
				<BsThreeDotsVertical style={{ cursor: 'pointer' }} />
			</Dropdown>
		);
	};

	const updateRequest = (values) => {
		const formDataObject = {
			...updateModal,
			errorMessage: values?.errorMessage,
			isAdcreationSuccess:
				values?.isAdcreationSuccess || updateModal?.isAdcreationSuccess,
			isClinicalcreationSuccess:
				values?.isClinicalcreationSuccess ||
				updateModal?.isClinicalcreationSuccess,
			isSsocreationSuccess:
				values?.isSsocreationSuccess || updateModal?.isSsocreationSuccess,
			//isSspcreationSuccess:
				//values?.isSspcreationSuccess || updateModal?.isSspcreationSuccess,
			modifiedBy: userDetails?.userId,
		};

		// Convert array values to comma-separated strings
		Object.keys(formDataObject).forEach((key) => {
			if (Array.isArray(formDataObject[key])) {
				formDataObject[key] = formDataObject[key].join(', ');
			}
		});

		setUpdate({ loading: true, success: null, error: null });

		api
			.post({
				path: '/activatebatchentry',
				service: 'useronboarding',
				formdata: formDataObject,
			})
			.then(() => {
				setUpdate({
					loading: false,
					success: 'Updated successfully!',
					error: null,
				});
				message.success('Updated successfully!');
				fetchCreationStatus();
			})
			.catch((err) => {
				console.error('Error:', err.message || 'Something went wrong');
				setUpdate({
					loading: false,
					success: null,
					error: err.message || 'Request failed',
				});
				message.error('Request failed');
			})
			.finally(() => {
				setUpdateModal(null);
			});
	};

	// Define the columns for the AntD table
	const columns = [
		{
			title: 'User Name',
			dataIndex: 'userName',
			key: 'userName',
		},
		{
			title: 'NH Name',
			dataIndex: 'nhName',
			key: 'nhName',
		},
		{
			title: 'Full Name',
			dataIndex: 'fullName',
			key: 'fullName',
		},
		{
			title: 'AD Creation Success',
			dataIndex: 'isAdcreationSuccess',
			key: 'isAdcreationSuccess',
			render: (value) => renderStatus(value),
		},
		{
			title: 'SSO Creation Success',
			dataIndex: 'isSsocreationSuccess',
			key: 'isSsocreationSuccess',
			render: (value) => renderStatus(value),
		},
		// {
		// 	title: 'SSP Creation Success',
		// 	dataIndex: 'isSspcreationSuccess',
		// 	key: 'isSspcreationSuccess',
		// 	render: (value) => renderStatus(value),
		// },
		{
			title: 'Clinical Creation Success',
			dataIndex: 'isClinicalcreationSuccess',
			key: 'isClinicalcreationSuccess',
			render: (value) => renderStatus(value),
		},
		{
			title: 'Batch Status',
			dataIndex: 'batchStatus',
			key: 'batchStatus',
		},
		{
			title: 'Remarks',
			dataIndex: 'errorMessage',
			key: 'errorMessage',
		},
		{
			title: 'Batch Actual Start Date',
			dataIndex: 'batchActualStartDate',
			key: 'batchActualStartDate',
			render: (value) => value && moment(value).format('DD-MM-YYYY'),
		},
		{
			title: 'Batch Update Date',
			dataIndex: 'batchUpdateDate',
			key: 'batchUpdateDate',
			render: (value) => value && moment(value).format('DD-MM-YYYY'),
		},
		{
			title: 'Batch Compeleted Date',
			dataIndex: 'batchCompletedDate',
			key: 'batchCompletedDate',
			render: (value) => value && moment(value).format('DD-MM-YYYY'),
		},
		{
			title: 'Action',
			dataIndex: 'action',
			key: 'action',
			fixed: 'right',
			width: 100,
			render: (value, row) => renderRowActions(row),
		},
	];

	const modalStyles = {
		header: {
			borderBottom: `1px solid #E0E1E2`,
			paddingBottom: 5,
		},
	};

	return (
		<div className={styles['wrapper']}>
			<div className={styles['top-section']}>
				<div className={styles['top-section-left']}>
					<div className={styles['left']}>
						<p className={styles['title']}>All Creation Status</p>
						<div className={styles['totals']}>
							<img
								className={styles['success-icons']}
								src={SuccessIcon}
								alt="Success Icon"
							/>
							<p className={styles['totals']}>
								Total Insights : {tableParams.pagination.total}
							</p>
						</div>
					</div>
					<div className={styles['right']}>
						<Input
							prefix={<IoSearchOutline />}
							className={styles['search-input']}
							placeholder="Search by full name"
							onPressEnter={handleSearch}
							onChange={debouncedSearch}
							allowClear
						/>
					</div>
				</div>
			</div>

			<Table
				className={styles['table']}
				columns={columns}
				rowKey={(record) => record.id}
				dataSource={data}
				pagination={{
					...tableParams.pagination,
					showSizeChanger: true,
					showQuickJumper: true,
					hideOnSinglePage: true,
				}}
				loading={loading}
				onChange={handleTableChange}
				scroll={{
					x: 'max-content',
				}}
			/>

			<Modal
				title={<p>Update Deactivation Status</p>}
				open={updateModal}
				onCancel={() => {
					setUpdateModal(null);
					form.resetFields();
				}}
				destroyOnClose
				centered
				footer={false}
				styles={modalStyles}
				width={600}
			>
				<Form
					id="user-creation-status-form"
					name="user-creation-status-form"
					form={form}
					labelCol={{ span: 24 }}
					onFinish={updateRequest}
					disabled={update.loading}
					validateTrigger={['onBlur', 'onChange']}
				>
					<Row gutter={20}>
						{!updateModal?.isAdcreationSuccess && (
							<Col xs={24} md={12}>
								<Form.Item
									label="AD Creation Success"
									name="isAdcreationSuccess"
									rules={[
										{
											required: true,
											message: 'Please select AD Creation status!',
										},
										{
											validator: (_, value) => {
												if (value === true) {
													return Promise.resolve();
												}
												return Promise.reject(
													new Error('You must select "Yes" to proceed.')
												);
											},
										},
									]}
								>
									<Radio.Group>
										<Radio value={true}>Yes</Radio>
										<Radio value={false}>No</Radio>
									</Radio.Group>
								</Form.Item>
							</Col>
						)}

						{!updateModal?.isSsocreationSuccess && (
							<Col xs={24} md={12}>
								<Form.Item
									label="SSO Creation Success"
									name="isSsocreationSuccess"
									rules={[
										{
											required: true,
											message: 'Please select SSO Creation status!',
										},
										{
											validator: (_, value) => {
												if (value === true) {
													return Promise.resolve();
												}
												return Promise.reject(
													new Error('You must select "Yes" to proceed.')
												);
											},
										},
									]}
								>
									<Radio.Group>
										<Radio value={true}>Yes</Radio>
										<Radio value={false}>No</Radio>
									</Radio.Group>
								</Form.Item>
							</Col>
						)}

						{/* {!updateModal?.isSspcreationSuccess && (
							<Col xs={24} md={12}>
								<Form.Item
									label="SSP Creation Success"
									name="isSspcreationSuccess"
									rules={[
										{
											required: true,
											message: 'Please select SSP Creation status!',
										},
										{
											validator: (_, value) => {
												if (value === true) {
													return Promise.resolve();
												}
												return Promise.reject(
													new Error('You must select "Yes" to proceed.')
												);
											},
										},
									]}
								>
									<Radio.Group>
										<Radio value={true}>Yes</Radio>
										<Radio value={false}>No</Radio>
									</Radio.Group>
								</Form.Item>
							</Col>
						)} */}

						{!updateModal?.isClinicalcreationSuccess && (
							<Col xs={24} md={12}>
								<Form.Item
									label="Clinical Creation Success"
									name="isClinicalcreationSuccess"
									rules={[
										{
											required: true,
											message: 'Please select Clinical Creation status!',
										},
										{
											validator: (_, value) => {
												if (value === true) {
													return Promise.resolve();
												}
												return Promise.reject(
													new Error('You must select "Yes" to proceed.')
												);
											},
										},
									]}
								>
									<Radio.Group>
										<Radio value={true}>Yes</Radio>
										<Radio value={false}>No</Radio>
									</Radio.Group>
								</Form.Item>
							</Col>
						)}

						{/* Remarks */}
						<Col xs={24}>
							<Form.Item
								label="Remarks"
								name="errorMessage"
								rules={[
									{
										required: true,
										message: 'Please enter remarks!',
									},
									({ getFieldValue }) => ({
										validator(_, value) {
											if (!value || value !== updateModal?.errorMessage) {
												return Promise.resolve();
											}
											return Promise.reject(
												new Error(
													'Remarks must be different from the previous one!'
												)
											);
										},
									}),
								]}
							>
								<Input.TextArea
									placeholder="Please enter Remarks!"
									autoSize={{
										minRows: 5,
										maxRows: 5,
									}}
								/>
							</Form.Item>
						</Col>
						<Col xs={24}>
							<Form.Item style={{ marginBottom: 0 }}>
								<Button
									loading={update.loading}
									type="primary"
									htmlType="submit"
									block
								>
									Update Status
								</Button>
							</Form.Item>
						</Col>
					</Row>
				</Form>
			</Modal>
		</div>
	);
};

export default CreationStatus;
